/**
 * Stripe Payment Element helpers for the membership wizard.
 */

const SATOSHI_CSS = 'https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700&display=swap';

const APPEARANCE = {
    theme: 'stripe',
    variables: {
        fontFamily: 'Satoshi, system-ui, sans-serif',
        fontSizeBase: '16px',
        colorPrimary: '#244536',
        colorText: '#20251f',
        colorTextSecondary: '#686e68',
        colorTextPlaceholder: '#686e68',
        colorDanger: '#c0392b',
        colorBackground: '#ffffff',
        borderRadius: '10px',
        spacingUnit: '4px',
    },
    rules: {
        '.Input': {
            border: '1px solid #cbd2c9',
            boxShadow: 'none',
            padding: '14px 16px',
        },
        '.Input:focus': {
            border: '1px solid #244536',
            boxShadow: 'none',
        },
        '.Label': {
            color: '#686e68',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '20px',
        },
        '.Tab': {
            border: '1px solid #cbd2c9',
            borderRadius: '10px',
            backgroundColor: '#ffffff',
        },
        '.Tab--selected': {
            border: '1.5px solid #244536',
            backgroundColor: '#dde5d8',
            color: '#20251f',
        },
        '.TabIcon--selected': {
            fill: '#244536',
        },
    },
};

function getStripeConfig() {
    return typeof pe_mp_stripe !== 'undefined' ? pe_mp_stripe : null;
}

function getStripe() {
    const config = getStripeConfig();
    if (!config?.publishableKey || typeof window.Stripe !== 'function') {
        return null;
    }
    return window.Stripe(config.publishableKey);
}

function planToTier(plan) {
    return plan === 'core-plus' ? 'coreplus' : plan === 'core' ? 'core' : '';
}

function membershipReturnUrl(tier, billing) {
    const config = getStripeConfig();
    const returnUrl = new URL(config?.returnUrl || window.location.href, window.location.origin);
    returnUrl.searchParams.set('membership_stripe', '1');
    if (billing === 'yearly') {
        returnUrl.searchParams.set('membership_billing', 'yearly');
    }
    returnUrl.hash = tier === 'core' ? 'join-core' : 'join-core-plus';
    return returnUrl.toString();
}

function stripeCountryCode(country) {
    const code = String(country || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : '';
}

function billingDetailsPayload({ name, email, country, postalCode }) {
    return {
        name: String(name || '').trim(),
        email: String(email || '').trim(),
        phone: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            country: stripeCountryCode(country),
            postal_code: String(postalCode || '').trim(),
        },
    };
}

export function getPaymentElementHost() {
    return document.querySelector('#payment-element');
}

export async function startMembershipCheckout({ name, email, country, postalCode, plan, newsletterOptIn, billing, promoCode, channel }) {
    const config = getStripeConfig();
    const stripe = getStripe();
    const host = getPaymentElementHost();
    const tier = planToTier(plan);
    const period = billing === 'yearly' ? 'yearly' : 'monthly';

    if (!config?.subscribeUrl || !stripe || !host || !tier) {
        throw new Error('Payments are temporarily unavailable.');
    }

    const response = await fetch(config.subscribeUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...(config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {}),
        },
        body: JSON.stringify({
            nonce: config.nonce,
            name,
            email,
            tier,
            country,
            postal_code: postalCode,
            newsletter_opt_in: newsletterOptIn ? 1 : 0,
            billing: period,
            promo_code: promoCode || '',
            channel: channel === 'waitlist' ? 'waitlist' : '',
        }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.client_secret == null) {
        const message = payload?.message || payload?.data?.message || 'Could not start checkout. Please try again.';
        throw new Error(message);
    }

    const elements = stripe.elements({
        clientSecret: payload.client_secret,
        appearance: APPEARANCE,
        fonts: [{ cssSrc: SATOSHI_CSS }],
        loader: 'auto',
    });

    const paymentElement = elements.create('payment', {
        layout: 'tabs',
        fields: {
            billingDetails: {
                name: 'never',
                email: 'never',
                phone: 'never',
                address: 'never',
            },
        },
        defaultValues: {
            billingDetails: billingDetailsPayload({ name, email, country, postalCode }),
        },
        wallets: {
            applePay: 'auto',
            googlePay: 'auto',
        },
    });

    const intentId = paymentIntentIdFromClientSecret(payload.client_secret);
    paymentElement.on('ready', () => {
        reportMembershipPaymentStatus(intentId, { kind: 'form_ready' });
    });
    paymentElement.on('loaderror', (event) => {
        reportMembershipPaymentStatus(intentId, {
            kind: 'form_failed',
            message: event?.error?.message || 'Payment form failed to load',
        });
    });

    host.replaceChildren();
    paymentElement.mount('#payment-element');

    return {
        stripe,
        elements,
        paymentElement,
        clientSecret: payload.client_secret,
        subscriptionId: payload.subscription_id || '',
        customerId: payload.customer_id || '',
        email,
        name,
        plan,
        tier,
        billing: period,
        country: String(country || '').trim().toUpperCase(),
        newsletterOptIn: Boolean(newsletterOptIn),
        promoCode: promoCode || '',
        channelHint: channel === 'waitlist' ? 'waitlist' : '',
        channel: payload.channel || '',
        amountDue: Number.isFinite(Number(payload.amount_due)) ? Number(payload.amount_due) : 0,
        discountLabel: payload.discount_label || '',
        nextChargeAt: Number(payload.next_charge_at) || 0,
    };
}

export function unmountPaymentElement(session) {
    try {
        session?.paymentElement?.unmount();
    } catch (error) {
        // Stripe unmount is safe to ignore if already gone.
    }

    const host = getPaymentElementHost();
    if (host) {
        host.replaceChildren();
    }
}

export async function confirmMembershipPayment({ stripe, elements, name, email, country, postalCode, clientSecret, tier, billing }) {
    if (!stripe || !elements || !clientSecret) {
        throw new Error('Payments are temporarily unavailable.');
    }

    const submitted = await elements.submit();
    if (submitted?.error) {
        throw stripePaymentError(submitted.error, 'Please check your payment details.');
    }

    const billingDetails = billingDetailsPayload({ name, email, country, postalCode });
    if (!billingDetails.address.country && !billingDetails.address.postal_code) {
        throw new Error('Please choose your country and enter a ZIP / postal code.');
    }

    const confirmResult = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
            return_url: membershipReturnUrl(tier, billing),
            payment_method_data: {
                billing_details: billingDetails,
            },
        },
        redirect: 'if_required',
    });

    if (confirmResult?.error) {
        throw stripePaymentError(confirmResult.error, 'Payment failed. Please try another method.');
    }

    return confirmResult;
}

function stripePaymentError(stripeError, fallback) {
    const error = new Error(stripeError?.message || fallback);
    const type = String(stripeError?.type || '');
    const code = String(stripeError?.code || '');
    const decline = String(stripeError?.decline_code || '');
    const message = String(stripeError?.message || '');
    const isDecline = type === 'card_error'
        || code === 'card_declined'
        || decline !== ''
        || message.includes('known test card');
    if (type !== 'validation_error' && isDecline) {
        error.reportToSlack = {
            message,
            decline_code: decline || code,
        };
    }
    return error;
}

const PROMO_STORAGE_KEY = 'pe_mp_membership_promo';
const CHANNEL_STORAGE_KEY = 'pe_mp_membership_channel';

export function rememberMembershipCampaign() {
    try {
        const params = new URLSearchParams(window.location.search);
        const code = (params.get('code') || '').trim().slice(0, 40);
        if (code) {
            sessionStorage.setItem(PROMO_STORAGE_KEY, code);
        }
        if ((params.get('utm_source') || '').toLowerCase() === 'waitlist') {
            sessionStorage.setItem(CHANNEL_STORAGE_KEY, 'waitlist');
        }
    } catch (error) {
        // sessionStorage can be blocked. Checkout still works without the code.
    }
}

export function membershipCampaign() {
    try {
        return {
            code: sessionStorage.getItem(PROMO_STORAGE_KEY) || '',
            channel: sessionStorage.getItem(CHANNEL_STORAGE_KEY) === 'waitlist' ? 'waitlist' : '',
        };
    } catch (error) {
        return { code: '', channel: '' };
    }
}

export function clearMembershipCampaign() {
    try {
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
        sessionStorage.removeItem(CHANNEL_STORAGE_KEY);
    } catch (error) {
        // Ignore a blocked sessionStorage.
    }
}

export function formatMembershipMoney(cents, currency = 'eur') {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) {
        return '';
    }
    const whole = Math.abs(amount - Math.round(amount)) < 0.001;
    const text = whole ? String(Math.round(amount)) : amount.toFixed(2);
    return String(currency).toLowerCase() === 'eur' ? `€${text}` : `${text} ${String(currency).toUpperCase()}`;
}

export function formatMembershipDate(unix) {
    const date = new Date(Number(unix) * 1000);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}

export async function validateMembershipPromo({ code, plan, billing, channel }) {
    const config = getStripeConfig();
    const tier = planToTier(plan);
    if (!config?.promoUrl || !tier) {
        throw new Error('Payments are temporarily unavailable.');
    }

    const response = await fetch(config.promoUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...(config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {}),
        },
        body: JSON.stringify({
            nonce: config.nonce,
            code,
            tier,
            billing: billing === 'yearly' ? 'yearly' : 'monthly',
            channel: channel === 'waitlist' ? 'waitlist' : '',
        }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.success) {
        const message = payload?.message || 'This code is not valid.';
        throw new Error(message);
    }

    return payload;
}

export function paymentIntentIdFromClientSecret(clientSecret) {
    const id = String(clientSecret || '').split('_secret_')[0];
    return id.startsWith('pi_') ? id : '';
}

export function reportMembershipPaymentStatus(paymentIntentId, detail) {
    const config = getStripeConfig();
    const id = String(paymentIntentId || '').trim();
    if (!config?.paymentStatusUrl || !/^pi_[A-Za-z0-9]+$/.test(id)) {
        return;
    }

    const kind = String(detail?.kind || '').replace(/[^a-z_]/g, '');
    const message = String(detail?.message || '').replace(/[\r\n]+/g, ' ').slice(0, 300);
    const decline = String(detail?.decline_code || '').replace(/[^a-z0-9_]/gi, '').slice(0, 64);

    fetch(config.paymentStatusUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...(config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {}),
        },
        body: JSON.stringify({
            nonce: config.nonce,
            payment_intent_id: id,
            kind,
            error_message: message,
            decline_code: decline,
        }),
        keepalive: true,
    }).catch(() => {});
}

export function getStripeReturnStatus() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('membership_stripe') !== '1') {
        return '';
    }
    return params.get('redirect_status') || '';
}

export function clearStripeReturnParams() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('membership_stripe') && !url.searchParams.has('redirect_status')) {
        return;
    }
    url.searchParams.delete('membership_stripe');
    url.searchParams.delete('redirect_status');
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('membership_billing');
    if (url.hash === '#join-core' || url.hash === '#join-core-plus') {
        url.hash = '';
    }
    window.history.replaceState({}, document.title, url.toString());
}
